<template>
  <GlobalWrapper
    :style="{ backgroundImage: 'url(' + campaign?.background + ')' }"
    class="py-20 bg-cover min-h-screen"
  >
    <div :class="loading || !campaignActive ? 'disabled' : null + 'mx-auto'">
      <div class="flex justify-between">
        <img :src="npo?.logo" alt="Non-profit logo" class="max-w-[11rem]" />
        <div class="flex flex-col space-y-2" v-if="userId">
          <a
            href=""
            @click.prevent="toggleQRCodeModal"
            class="text-center rounded bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
            :class="loading || !campaignActive ? 'disabled' : null"
            >Share QR Code</a
          >
          <a
            href="/campaigns"
            class="text-center rounded bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
            >View Dashboard</a
          >
        </div>
      </div>

      <Spinner v-if="loading" class="mt-10" />
      <div v-else class="relative mt-4">
        <!-- Personal & Contact Details -->
        <div>
          <LeadFormInfo @submitForm="onSubmitForm" />
        </div>
      </div>
    </div>
    <Modal
      headerText="Thank you for your info!"
      ctaText="Close and Reset Form"
      :modalOpen="successModalOpen"
      @closeModal="toggleSuccessModal"
      :showSuccessIcon="true"
    />
    <Modal
      headerText="Info submission failed :("
      ctaText="Close and Try Again"
      :subheaderText="'Reason: ' + donateFailureMessage"
      :modalOpen="failureModalOpen"
      @closeModal="toggleFailureModal"
      :showFailureIcon="true"
    />
    <Modal
      headerText="Scan the QR code to keep in touch!"
      ctaText="Close"
      :modalOpen="qrCodeModalOpen"
      @closeModal="toggleQRCodeModal"
      :modalImgUrl="qrCodeImgUrl"
    />
    <Toast v-if="loading" text="Loading..." />
    <Toast v-else-if="!loading && !campaignActive" :text="disabledMessage" />
  </GlobalWrapper>
</template>

<script>
import { ref, onBeforeMount, computed } from 'vue'
import { useStore } from 'vuex'
import GlobalWrapper from '../components/GlobalWrapper'
import Modal from '@/components/Modal'
import Toast from '@/components/Toast'
import Spinner from '@/components/Spinner'
import { useRoute } from 'vue-router'
import { LeadApi } from '@/services/leads/'
import { CampaignApi } from '@/services/campaign/'
import SelectField from '@/components/inputs/SelectField'
import InputField from '@/components/inputs/InputField'
import LeadFormInfo from '@/components/LeadFormInfo'
import {
  formTokenQuery,
  renderQrCodeDataUrl,
  requestDonorFormToken,
  resolveFormToken,
} from '@/services/formAccess'

export default {
  name: 'CampaignLead',
  components: {
    GlobalWrapper,
    Modal,
    LeadFormInfo,
    SelectField,
    InputField,
    Toast,
    Spinner,
  },
  setup() {
    const store = useStore()
    const userToken = store.getters.token
    const userId = store.getters.user ? store.getters.user.id : ''
    const isFundraiser = store.getters.isFundraiser
    const npo = ref(null)
    const campaign = ref(null)
    const successModalOpen = ref(false)
    const failureModalOpen = ref(false)
    const qrCodeModalOpen = ref(false)
    const qrCodeImgUrl = ref('')
    let donateFailureMessage = ref('')

    // Get data for displaying bg image and NPO logo in the form
    const route = useRoute()
    const campaignId = route.params.id
    // Anonymous QR devices identify their fundraiser with a signed, expiring grant.
    const formToken = resolveFormToken(route)
    const campaignQueryParams = formTokenQuery(formToken)
    const campaignApiUrl = `/campaigns/${campaignId}/${campaignQueryParams}`
    let loading = ref(false)

    const campaignActive = computed(() => {
      return campaign.value && campaign.value?.active && campaign.value?.collectingLeads
    })

    // Submit the lead form
    const onSubmitForm = async (data) => {
      loading.value = true

      const leadData = {
        ...data,
        campaign: campaignId,
        formToken,
      }

      await createLead(leadData)
      loading.value = false
    }

    const createLead = async (leadData) => {
      try {
        await LeadApi.csc.customCreate(leadData)
        toggleSuccessModal()
      } catch (e) {
        handleFailure('Failed to create lead', e)
      }
    }

    onBeforeMount(async () => {
      loading.value = true
      try {
        campaign.value = await CampaignApi.csc.customRetrieve({
          campaign: campaignId,
          formToken,
        })

        npo.value = campaign.value.npo
      } catch (e) {
        console.log(e)
      } finally {
        loading.value = false
      }
    })

    const handleFailure = (label, error) => {
      console.log(label, error)
      donateFailureMessage.value = error.message
      toggleFailureModal()
      loading.value = false
    }

    const toggleSuccessModal = () => {
      if (successModalOpen.value) {
        location.reload()
      }
      successModalOpen.value = !successModalOpen.value
    }

    // Display failure modal if the donation lead info fails to create
    const toggleFailureModal = () => {
      failureModalOpen.value = !failureModalOpen.value
    }

    const getQRCode = async () => {
      // Signed, expiring grant instead of the fundraiser's id, rendered locally so the
      // credential is never handed to an external image service.
      const token = await requestDonorFormToken(campaignId)
      const url = `${location.host}${route.fullPath}?form_token=${encodeURIComponent(token)}`
      qrCodeImgUrl.value = await renderQrCodeDataUrl(url, 150)
    }

    const toggleQRCodeModal = async () => {
      if (!qrCodeModalOpen.value) {
        await getQRCode()
      }
      qrCodeModalOpen.value = !qrCodeModalOpen.value
    }

    const disabledMessage = computed(() => {
      let reasons = []
      let message = ''

      if (!campaign.value) {
        if (isFundraiser) {
          message =
            'You do not have access to this campaign. Please contact your organization admin for further assistance.'
        } else {
          message =
            'There was an error retrieving the campaign details. Please refresh the page or contact an admin for further assistance.'
        }
      } else {
        message = 'This campaign is inactive.'

        !campaign.value?.collectingLeads && reasons.push(" It's marked as 'Not Accepting Leads'.")

        if (reasons.length) {
          message += reasons[0]
        }
      }
      return message
    })

    return {
      userToken,
      userId,
      campaign,
      npo,
      LeadFormInfo,
      successModalOpen,
      failureModalOpen,
      campaignApiUrl,
      campaignId,
      onSubmitForm,
      toggleSuccessModal,
      toggleFailureModal,
      campaignActive,
      toggleQRCodeModal,
      qrCodeModalOpen,
      qrCodeImgUrl,
      loading,
      donateFailureMessage,
      disabledMessage,
    }
  },
}
</script>
